import { randomBytes } from "./crypto.js";
import { createEmitter } from "./emitter.js";
import { applyProposal, flattenChains, proposeNamespaces, uniqueAdapters, } from "./plugin.js";
import { openRelay, relays } from "./relay.js";
import { SessionClient } from "./session.js";
import { defaultStorage, loadSeed } from "./storage.js";
import { accountsByChain, ProviderRpcError, RpcErrorCode, } from "./types.js";
/**
 * An EIP-1193-compatible provider backed by a WalletConnect v2 session.
 *
 * Application code should call {@link Provider.init}; tests that need an isolated or injected
 * instance should call {@link Provider.create}.
 *
 * @example
 * ```ts
 * import { Provider } from "konekt";
 * import { ethereumMainnet } from "konekt/eip155";
 *
 * const provider = await Provider.init({
 *   projectId,
 *   metadata,
 *   chains: [ethereumMainnet],
 * });
 *
 * provider.on("display_uri", showPairingUri);
 * await provider.connect();
 * ```
 */
export class Provider {
    static #singleton;
    /** Allows provider consumers to identify this as a WalletConnect-backed provider. */
    isWalletConnect = true;
    /** The WalletConnect Cloud project ID this provider was configured with. */
    projectId;
    #events;
    #session;
    #chains;
    #adapters;
    #features;
    #active = new Map();
    /**
     * Creates or returns the application-wide provider.
     *
     * The first call fixes the provider options; later calls return the same promise. Konekt uses the
     * default relay and persistent browser storage unless the options override them. Register
     * `display_uri` and `request_sent` listeners before starting work that emits those events.
     *
     * @returns The shared provider, extended with properties supplied by the configured adapters.
     */
    static init(opts) {
        Provider.#singleton ??= Provider.create(opts);
        return Provider.#singleton;
    }
    /**
     * Creates an independent provider, primarily for tests.
     *
     * Pass `deps` to replace the relay, session, seed, or storage. Supplying `deps.session` keeps the
     * provider offline and does not open a real relay socket.
     *
     * @returns A new provider, extended with properties supplied by the configured adapters.
     */
    static async create(opts, deps = {}) {
        const storage = deps.storage ?? (opts.storage === null ? undefined : (opts.storage ?? defaultStorage()));
        const seed = deps.seed ?? (storage ? await loadSeed(storage) : randomBytes(32));
        const provider = new Provider(opts, { ...deps, seed, storage });
        if (!deps.session)
            await provider.#restore();
        return provider;
    }
    /**
     * Constructs a provider synchronously.
     *
     * Prefer {@link Provider.init} in applications and {@link Provider.create} in tests because those
     * methods perform asynchronous seed loading and session restoration.
     */
    constructor(opts, deps = {}) {
        this.projectId = opts.projectId;
        this.#events = createEmitter();
        this.#chains = flattenChains(opts.chains);
        if (!this.#chains.length)
            throw new Error("UNSUPPORTED_CHAINS");
        this.#adapters = uniqueAdapters(this.#chains);
        this.#features = opts.features ?? [];
        for (const c of this.#chains) {
            if (!this.#active.has(c.namespace))
                this.#active.set(c.namespace, c.id);
        }
        if (deps.session) {
            this.#session = deps.session;
            this.#applyExtensions();
            return;
        }
        const seed = deps.seed ?? randomBytes(32);
        const openSession = (relay) => new SessionClient({
            relay,
            metadata: opts.metadata,
            namespaces: proposeNamespaces(this.#chains),
            onProposal: (p) => applyProposal(this.#features, p),
            onUri: (uri) => this.#events.emit("display_uri", uri),
            onEvent: (e) => this.#onSessionEvent(e),
            storage: deps.storage,
            onDebug: opts.onDebug,
            onRequestSent: (e) => this.#events.emit("request_sent", e),
            ttl: opts.ttl,
        });
        if (deps.relay) {
            this.#session = openSession(deps.relay);
            this.#applyExtensions();
            return;
        }
        const relay = openRelay({ projectId: opts.projectId, seed, url: opts.relayUrl, onDebug: opts.onDebug });
        this.#session = openSession(relay);
        relays.set(this, relay);
        this.#applyExtensions();
    }
    #ctx() {
        return {
            session: () => this.#session.session,
            emit: (event, payload) => this.#events.emit(event, payload),
            chains: this.#chains,
            activeChainId: (ns) => this.#active.get(ns),
            setActiveChainId: (ns, id) => {
                this.#active.set(ns, id);
            },
            forward: (req) => this.#forward(req),
        };
    }
    #configured(chainId) {
        if (this.#chains.some((c) => c.id === chainId))
            return chainId;
        throw new ProviderRpcError(RpcErrorCode.invalidParams, `Chain "${chainId}" is not configured. Add it to chains before targeting it.`);
    }
    #forward(req) {
        if (!this.#session.session)
            throw new ProviderRpcError(RpcErrorCode.unauthorized, `No session. Call connect() before ${req.method}.`);
        return this.#session.request(req);
    }
    #applyExtensions() {
        const ctx = this.#ctx();
        for (const adapter of this.#adapters) {
            const ext = adapter.extend?.(ctx);
            if (!ext)
                continue;
            Object.defineProperties(this, Object.getOwnPropertyDescriptors(ext));
        }
    }
    async #restore() {
        if ((await this.#session.restore()) && this.#session.session) {
            const ctx = this.#ctx();
            for (const adapter of this.#adapters)
                adapter.onSettle?.(this.#session.session, ctx);
        }
    }
    /** The approved session, or `undefined` before connection and after disconnection. */
    get session() {
        return this.#session.session;
    }
    /** Whether the provider currently has an approved session. */
    get connected() {
        return !!this.#session.session;
    }
    /** The temporary pairing URI for the current proposal, when one exists. */
    get uri() {
        return this.#session.uri;
    }
    /**
     * The configured chains this provider proposes, flattened into one list.
     *
     * This is configuration, not proof that the wallet approved every chain. Read {@link session} to
     * inspect the approved namespaces.
     */
    get chains() {
        return this.#chains;
    }
    /**
     * Approved addresses grouped by CAIP-2 chain ID across all namespaces in the current session.
     *
     * @example
     * { "eip155:1": ["0x…"], "cosmos:cosmoshub-4": ["cosmos1…"] }
     */
    get accountsByChain() {
        return accountsByChain(this.#session.session?.namespaces);
    }
    /** Adds an event listener that runs every time the event is emitted. */
    on(e, fn) {
        this.#events.on(e, fn);
    }
    /** Adds an event listener that removes itself after its first call. */
    once(e, fn) {
        this.#events.once(e, fn);
    }
    /** Removes a listener previously passed to {@link on} or {@link once}. */
    off(e, fn) {
        this.#events.off(e, fn);
    }
    /** Alias for {@link off}, provided for EIP-1193 and Node-style event compatibility. */
    removeListener(e, fn) {
        this.#events.off(e, fn);
    }
    /**
     * Proposes a WalletConnect session and waits for the wallet to approve or reject it.
     *
     * Listen for `display_uri` before calling this method. Pass an `AbortSignal` so closing the
     * pairing UI can cancel the attempt. If a feature rejects the settled session, this method
     * disconnects it before rejecting.
     *
     * @returns The approved WalletConnect session.
     */
    async connect({ signal } = {}) {
        const s = await this.#session.connect(signal);
        const ctx = this.#ctx();
        for (const adapter of this.#adapters)
            adapter.onSettle?.(s, ctx);
        try {
            for (const f of this.#features)
                await f.onSettle?.(s);
        }
        catch (e) {
            // The session is already settled and persisted. A feature that rejects it must not leave one behind.
            await this.disconnect().catch(() => { });
            throw e;
        }
        const evmChainId = this.chainId;
        this.#events.emit("connect", evmChainId === undefined ? {} : { chainId: `0x${evmChainId.toString(16)}` });
        return s;
    }
    /** Ends the current session, clears adapter and feature state, and emits `disconnect`. */
    async disconnect() {
        await this.#session.disconnect();
        for (const adapter of this.#adapters)
            adapter.onDisconnect?.();
        for (const f of this.#features)
            f.onDisconnect?.();
        this.#events.emit("disconnect", { code: 6000, message: "User disconnected" });
    }
    /**
     * Legacy EIP-1193 shortcut that connects if needed and returns the approved EVM accounts.
     *
     * New code can call {@link connect} and then read the EVM adapter's `accounts` property.
     */
    async enable() {
        if (!this.#session.session)
            await this.connect();
        return (await this.request({ method: "eth_requestAccounts" }));
    }
    /**
     * Sends an EIP-1193 request through the adapter that supports its method.
     *
     * The optional `chainId` is a configured CAIP-2 ID such as `"eip155:8453"`. It targets this call
     * only and does not change the active chain. Wallet methods require a connected session; EVM
     * JSON-RPC reads require a `read` transport on the selected chain.
     *
     * @throws {@link ProviderRpcError} with code 4100 when a wallet method has no session, 4200 when
     * the method or read transport is unsupported, or -32602 for malformed parameters.
     */
    async request({ method, params }, chainId) {
        const req = { method, params, chainId: chainId && this.#configured(chainId) };
        const ctx = this.#ctx();
        for (const adapter of this.#adapters) {
            const out = await adapter.handle?.(req, ctx);
            if (out !== undefined)
                return out;
        }
        throw new ProviderRpcError(RpcErrorCode.unsupportedMethod, `Unsupported method: ${method}`);
    }
    #onSessionEvent(e) {
        const ctx = this.#ctx();
        switch (e.type) {
            case "event":
                for (const adapter of this.#adapters)
                    adapter.onEvent?.(e.name, e.data, e.chainId, ctx);
                break;
            case "update":
                for (const adapter of this.#adapters)
                    adapter.onEvent?.("session_update", e.namespaces, undefined, ctx);
                break;
            case "delete":
                for (const adapter of this.#adapters)
                    adapter.onDisconnect?.();
                for (const f of this.#features)
                    f.onDisconnect?.();
                this.#events.emit("disconnect", { code: e.code, message: e.message });
                break;
        }
    }
}
//# sourceMappingURL=provider.js.map